import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Header } from "@/components/layout/Header";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ProfileHeader } from "@/components/child-profile/ProfileHeader";
import { EditProfileDialog } from "@/components/child-profile/EditProfileDialog";
import { MobileViewSelector } from "@/components/child-profile/MobileViewSelector";
import { DesktopTabs } from "@/components/child-profile/DesktopTabs";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { OverviewSection } from "@/components/child-profile/OverviewSection";
import { TestProgress } from "@/components/dashboard/TestProgress";
import { Card, CardContent } from "@/components/ui/card";
import { TestResultsList } from "@/components/child-profile/TestResultsList";
import { MilestonesSection } from "@/components/child-profile/MilestonesSection";
import { TestingModulesSection } from "@/components/child-profile/TestingModulesSection";

const ChildProfileDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const isMobile = useIsMobile();

  const { data: child, isLoading, error } = useQuery({
    queryKey: ["childProfile", id],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("No authenticated session");
      }

      const { data, error } = await supabase
        .from("child_profiles")
        .select("*")
        .eq('id', id)
        .single();

      if (error) {
        console.error("Error fetching child profile:", error);
        throw error;
      }

      if (data.parent_id !== session.user.id) {
        throw new Error("Access denied");
      }

      return data;
    },
    retry: false,
    enabled: !!id
  });

  const handleDelete = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("No authenticated session");
      }

      const { error } = await supabase
        .from("child_profiles")
        .delete()
        .eq('id', id)
        .eq('parent_id', session.user.id);

      if (error) throw error;

      queryClient.setQueryData(["childProfiles"], (oldData: any) => {
        if (!oldData) return [];
        return oldData.filter((profile: any) => profile.id !== id);
      });

      queryClient.removeQueries({ queryKey: ["childProfile", id] });
      await queryClient.invalidateQueries({ queryKey: ["childProfiles"] });

      toast({
        title: "Success",
        description: "Profile deleted successfully",
      });
      
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete profile",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : "An error occurred";
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-500">{errorMessage}</p>
        <Button onClick={() => navigate("/dashboard")}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p>Profile not found or access denied</p>
        <Button onClick={() => navigate("/dashboard")}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  const tabContent = {
    overview: <OverviewSection />,
    progress: <TestProgress childId={child.id} />,
    "testing-modules": <TestingModulesSection childId={child.id} />,
    tests: (
      <Card>
        <CardContent className="p-6">
          <TestResultsList childId={child.id} />
        </CardContent>
      </Card>
    ),
    milestones: <MilestonesSection childId={child.id} />
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <ProfileHeader
          name={child.name}
          age={child.age}
          birthday={child.birthday}
          profilePictureUrl={child.profile_picture_url}
          onEdit={() => setIsEditDialogOpen(true)}
          onDelete={handleDelete}
        />

        <EditProfileDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          childId={child.id}
          childData={{
            name: child.name,
            age: child.age,
            birthday: child.birthday,
            profile_picture_url: child.profile_picture_url,
          }}
        />

        <div className="mt-6">
          {isMobile ? (
            <>
              <MobileViewSelector
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
              <div className="mt-4">
                {tabContent[activeTab as keyof typeof tabContent]}
              </div>
            </>
          ) : (
            <DesktopTabs childId={child.id} />
          )}
        </div>
      </main>
    </div>
  );
};

export default ChildProfileDetails;